import React, { useState, useEffect } from 'react'
import { T, Stat, SectionLabel, Spinner, Empty, RECO, pct, MARKET_LABEL } from './ui'
import { shareProof } from './share'
import * as api from './api'

/**
 * Public NHL track-record page (/nhl/proof) — gate-free, settled results only. The
 * shareable case for the product: the recommended record graded at the real book
 * line, realized CLV, and the hit rate by conviction tier. No live board.
 */
export default function NHLProofPage() {
  const [data, setData] = useState(null)
  useEffect(() => { api.getNHLProof().then(setData) }, [])

  const share = () => { shareProof().catch(() => {}) }

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.text, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 460, margin: '0 auto', padding: '20px 16px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.03em' }}>
            LINE<span style={{ color: T.green }}>BRKR</span>
            <span style={{ marginLeft: 7, fontSize: 14, color: T.ice, fontWeight: 800 }}>🏒 NHL</span>
            <span style={{ marginLeft: 7, fontSize: 10, fontWeight: 900, letterSpacing: '0.08em',
              color: '#1A1205', background: T.amber, borderRadius: 5, padding: '2px 6px',
              verticalAlign: 'middle' }}>BETA</span>
          </div>
          <button onClick={share} style={{ background: T.greenDim, color: T.green, border: `1px solid ${T.green}66`,
            borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Share</button>
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>
          Public track record — recommended plays only, graded at the real closing book line.
        </div>

        {data === null ? <Spinner /> : <Body data={data} />}

        <div style={{ fontSize: 10.5, color: T.faint, marginTop: 22, lineHeight: 1.6 }}>
          Research and analytics for entertainment only — not betting advice, not a sportsbook, no
          guaranteed outcomes. 21+ · Problem gambling? Call 1-800-GAMBLER.
        </div>
      </div>
    </div>
  )
}

function Body({ data }) {
  const sc = data.scorecard || {}
  const rec = sc.recommended || {}
  const real = sc.realized || {}
  const tiers = data.tiers || {}
  const wl = r => `${r.wins || 0}-${r.losses || 0}${r.pushes ? `-${r.pushes}` : ''}`
  const hasData = (sc.n_recommended || 0) > 0
  if (!hasData) return <Empty message="Track record builds as the season is graded." />
  return (
    <div>
      <SectionLabel>Recommended record</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        <Stat label="Record" value={wl(rec)} color={T.green} />
        <Stat label="Win %" value={rec.win_pct != null ? pct(rec.win_pct) : '—'} color={T.text} />
        <Stat label="Realized (book)" value={wl(real)} color={real.win_pct >= 0.5 ? T.green : T.amber} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8, marginTop: 8 }}>
        <Stat label="CLV positive" value={sc.clv_positive_rate != null ? pct(sc.clv_positive_rate) : '—'} color={T.ice} />
        <Stat label="Mean CLV" value={sc.clv_mean != null ? sc.clv_mean : '—'} color={T.text} />
      </div>

      <SectionLabel>By conviction tier</SectionLabel>
      {Object.keys(tiers).length ? Object.entries(tiers).map(([t, b]) => (
        <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px',
          borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ color: RECO.has(t) ? T.green : T.muted2, fontWeight: RECO.has(t) ? 800 : 600 }}>{t}</span>
          <span style={{ fontFamily: T.mono }}>{b.wins}-{b.losses} · {b.win_pct != null ? pct(b.win_pct) : '—'}</span>
        </div>
      )) : null}

      <SectionLabel>By market (realized)</SectionLabel>
      {Object.entries(sc.by_prop || {}).filter(([, v]) => (v.realized?.graded || 0) > 0).map(([mk, v]) => (
        <div key={mk} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 4px',
          borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
          <span style={{ color: T.muted2 }}>{MARKET_LABEL[mk] || mk}</span>
          <span style={{ fontFamily: T.mono }}>{wl(v.realized)} · {v.realized.win_pct != null ? pct(v.realized.win_pct) : '—'}</span>
        </div>
      ))}
    </div>
  )
}
